/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useEffect, useRef, useState } from 'react';

import { Hidden, Typography } from '@coursera/cds-core';

import { useEnterpriseAdminSearchContext } from 'bundles/enterprise-admin-product-search/context';
import SearchFilterGroup from 'bundles/search-common/components/filters/SearchFilterGroup';
import SearchFilterPlaceholder from 'bundles/search-common/components/filters/SearchFilterPlaceholder';
import SearchFiltersMobile from 'bundles/search-common/components/filters/SearchFiltersMobile';
import SkipToResultsLink from 'bundles/search-common/components/filters/SkipToResultsLink';
import { ENTERPRISE_COLLECTION_ENTITIES } from 'bundles/search-common/constants';
import type { SearchFilterEventingData, SearchFilterOption } from 'bundles/search-common/types';
import type { FacetConfig } from 'bundles/search-common/types/filter';

import _t from 'i18n!nls/search-common';

type Props = {
  showingNoResults?: boolean;
  filters?: Record<string, SearchFilterOption[] | undefined>;
  isMobile?: boolean;
  activeFilters?: string[];
  eventingData: SearchFilterEventingData;
  facetOrder?: string[];
  facets: FacetConfig[];
  addFilters: (filters: string) => void;
  removeFilters: (filters: string) => void;
  clearFilters: () => void;
  setFiltersByFacet?: (facetName: string, filters: string[]) => void;
  shouldHideSectionTitle?: boolean;
};

const styles = {
  placeholderContainer: css`
    padding-top: 30px;
  `,
  filterTitle: css`
    margin-top: 8px;
    margin-bottom: 32px;
  `,
};

const shallowEqualFilters = (
  a?: Record<string, SearchFilterOption[] | undefined>,
  b?: Record<string, SearchFilterOption[] | undefined>
): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const key of aKeys) {
    if (a[key] !== b[key]) return false;
  }
  return true;
};

export const SearchFilters = ({
  showingNoResults,
  filters,
  isMobile,
  eventingData,
  activeFilters,
  facetOrder,
  facets,
  addFilters,
  removeFilters,
  clearFilters,
  setFiltersByFacet,
  shouldHideSectionTitle,
}: Props) => {
  const isEnterpriseAdminSearch = useEnterpriseAdminSearchContext();

  // saving previous filters to prevent from showing placeholder and close modal
  const [previousFilters, setPreviousFilters] = useState(filters);
  const previousFiltersRef = useRef(previousFilters);
  useEffect(() => {
    if (!filters || !Object.keys(filters).length) return;

    // Avoid infinite update loops when upstream provides new object refs each render
    setPreviousFilters((prev) => {
      if (shallowEqualFilters(prev, filters)) return prev;
      previousFiltersRef.current = filters;
      return filters;
    });
  }, [filters]);
  const currentFilters = filters && Object.keys(filters).length ? filters : previousFiltersRef.current;

  // Filters are loading if 'filters' (from Algolia) is undefined or is an empty object
  // and facets (static list of facets) is defined (which means we are expecting filter data from Algolia).
  const filtersLoading = (!currentFilters || !Object.keys(currentFilters).length) && !!facets;

  if (showingNoResults) {
    return null;
  }

  if (isMobile) {
    return (
      <SearchFiltersMobile
        filters={currentFilters}
        facets={facets}
        eventingData={eventingData}
        activeFilters={activeFilters}
        filtersLoading={filtersLoading}
        addFilters={addFilters}
        removeFilters={removeFilters}
        clearFilters={clearFilters}
      />
    );
  }

  if (filtersLoading) {
    return (
      <div css={styles.placeholderContainer}>
        {Array.from(Array(7)).map((_item, index) => {
          // eslint-disable-next-line react/no-array-index-key
          return <SearchFilterPlaceholder key={index} isCompact={true} />;
        })}
      </div>
    );
  } else {
    return (
      <Hidden smDown>
        <SkipToResultsLink isMobile={isMobile} />
        {!shouldHideSectionTitle && (
          // eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2
          <Typography variant="h2semibold" component="h2" css={styles.filterTitle}>
            {_t('Filter by')}
          </Typography>
        )}
        {facets.map((facet) => {
          const attribute = facet.property;
          let items = currentFilters?.[attribute];

          if (isEnterpriseAdminSearch && items && attribute === 'productTypeDescription') {
            items = items?.filter((_) => ENTERPRISE_COLLECTION_ENTITIES.includes(_.label));
          }
          return (
            <SearchFilterGroup
              key={attribute}
              attribute={attribute}
              addFilters={addFilters}
              setFiltersByFacet={setFiltersByFacet}
              removeFilters={removeFilters}
              clearFilters={clearFilters}
              eventingData={{
                searchIndex: eventingData.searchIndex,
                searchIndexPosition: eventingData.searchIndexPosition,
                activeFilterItems: eventingData.filtersApplied,
              }}
              {...facet}
              items={items}
            />
          );
        })}
      </Hidden>
    );
  }
};

export default SearchFilters;
